/** A value JSON can hold. */
export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

/** Placeholder for an object that contains itself somewhere below. */
export const CIRCULAR = "[Circular]";

/**
 * Turns any value debug screens show (reactive objects, dates, maps, sets, big ints, cycles) into plain JSON, the way
 * `JSON.stringify` would write it: `undefined` and functions are left out of objects and become `null` in arrays,
 * dates become ISO strings and a cycle becomes `"[Circular]"` instead of throwing.
 */
export function toJsonValue(value: unknown): JsonValue {
  return convert(value, new WeakSet());
}

function convert(value: unknown, seen: WeakSet<object>): JsonValue {
  if (value === null || value === undefined) {
    return null;
  }
  switch (typeof value) {
    case "string":
    case "boolean":
      return value;
    case "number":
      return Number.isFinite(value) ? value : null;
    case "bigint":
      return value.toString();
    case "function":
    case "symbol":
      return null;
  }
  const object = value as object;
  if (seen.has(object)) {
    return CIRCULAR;
  }
  if (object instanceof Date) {
    return Number.isNaN(object.getTime()) ? null : object.toISOString();
  }
  const withToJson = object as { toJSON?: () => unknown };
  if (typeof withToJson.toJSON === "function") {
    return convert(withToJson.toJSON(), seen);
  }
  seen.add(object);
  try {
    if (Array.isArray(object) || object instanceof Set) {
      return Array.from(object as Iterable<unknown>, item => convert(item, seen));
    }
    const entries = object instanceof Map ? [...object.entries()] : Object.entries(object);
    const result: { [key: string]: JsonValue } = {};
    for (const [key, item] of entries) {
      if (item === undefined || typeof item === "function" || typeof item === "symbol") {
        continue;
      }
      result[String(key)] = convert(item, seen);
    }
    return result;
  } finally {
    seen.delete(object);
  }
}

/** The value as JSON text, indented by two spaces. */
export function formatJson(value: unknown): string {
  return JSON.stringify(toJsonValue(value), null, 2);
}

export type JsonTokenType = "key" | "string" | "number" | "boolean" | "null" | "punctuation" | "whitespace" | "other";

export interface JsonToken {
  type: JsonTokenType
  text: string
}

const TOKEN = /("(?:[^"\\\n]|\\.)*")(\s*:)?|("(?:[^"\\\n]|\\.)*)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false)\b|\b(null)\b|([{}[\],:])|(\s+)|([^\s"{}[\],:]+)/g;

/**
 * Splits JSON text into tokens for colouring. It never fails: text that is not valid JSON (while someone types) still
 * splits into tokens, and the tokens joined together are always exactly the input.
 */
export function tokenizeJson(text: string): JsonToken[] {
  const tokens: JsonToken[] = [];
  for (const match of text.matchAll(TOKEN)) {
    const [, string, colon, unterminated, number, boolean, nil, punctuation, whitespace, other] = match;
    if (string !== undefined) {
      tokens.push({ type: colon ? "key" : "string", text: string });
      if (colon) {
        tokens.push({ type: "punctuation", text: colon });
      }
    } else if (unterminated !== undefined) {
      tokens.push({ type: "string", text: unterminated });
    } else if (number !== undefined) {
      tokens.push({ type: "number", text: number });
    } else if (boolean !== undefined) {
      tokens.push({ type: "boolean", text: boolean });
    } else if (nil !== undefined) {
      tokens.push({ type: "null", text: nil });
    } else if (punctuation !== undefined) {
      tokens.push({ type: "punctuation", text: punctuation });
    } else if (whitespace !== undefined) {
      tokens.push({ type: "whitespace", text: whitespace });
    } else {
      tokens.push({ type: "other", text: other ?? match[0] });
    }
  }
  return tokens;
}

export type JsonParseResult
  = | { ok: true, value: JsonValue }
    | { ok: false, message: string, line: number | null, column: number | null };

/** Parses JSON text; on an error it reports where, as 1-based line and column when the engine gives a position. */
export function parseJson(text: string): JsonParseResult {
  try {
    return { ok: true, value: JSON.parse(text) as JsonValue };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const position = /position (\d+)/.exec(message);
    if (position) {
      const offset = Number(position[1]);
      const before = text.slice(0, offset).split("\n");
      return { ok: false, message, line: before.length, column: before.at(-1)!.length + 1 };
    }
    const lineColumn = /line (\d+) column (\d+)/.exec(message);
    if (lineColumn) {
      return { ok: false, message, line: Number(lineColumn[1]), column: Number(lineColumn[2]) };
    }
    return { ok: false, message, line: null, column: null };
  }
}
